import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import ScanLaunchLoading from '../components/ScanLaunchLoading';
import TermsModal from '../components/TermsModal';
import { useScanAllowance } from '../components/scanAllowance';
import {
  getCodebaseZipValidationError,
  requestFreeScan,
  requestScanAllowance,
  waitForMinimumScanLaunchLoading
} from '../components/workflowShared';

interface LandingPageProps {
  onNavigate: () => void;
}

type LaunchState = 'idle' | 'submitting' | 'error';

const SECURITY_TOOL_LOGOS = [
  {
    name: 'Trail of Bits',
    src: '/trail_of_bits_logo.jpg'
  },
  {
    name: 'Cyfrin',
    src: '/cyfrin.jpg'
  },
  {
    name: 'Pashov',
    src: '/pashovlogo.png'
  },
  {
    name: 'QuillShield',
    src: '/quillshield.avif'
  }
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CodebaseUploadIcon() {
  return (
    <svg className="landing-form-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 4v10m0-10 4 4m-4-4-4 4M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg className="button-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

const formatUploadSize = (sizeBytes: number | undefined): string => {
  if (!sizeBytes || sizeBytes <= 0) {
    return '';
  }

  if (sizeBytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(sizeBytes / 1024)).toLocaleString()} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getSelectedFileLabel = (file: File | null): string => {
  if (!file) {
    return 'Choose a .zip archive';
  }

  const sizeLabel = formatUploadSize(file.size);

  return `${file.name}${sizeLabel ? ` (${sizeLabel})` : ''}`;
};

function LandingPage({ onNavigate }: LandingPageProps) {
  const nextScanAt = useScanAllowance();
  const [email, setEmail] = useState('');
  const [codebaseFile, setCodebaseFile] = useState<File | null>(null);
  const [isWarningVisible, setIsWarningVisible] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [fileError, setFileError] = useState('');
  const [launchError, setLaunchError] = useState('');
  const [launchState, setLaunchState] = useState<LaunchState>('idle');
  const codebaseInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const trimmedEmail = email.trim();

  useEffect(() => {
    void requestScanAllowance().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (codebaseFile) {
      emailInputRef.current?.focus();
    }
  }, [codebaseFile]);

  const resetLaunchError = () => {
    if (launchState === 'error') {
      setLaunchState('idle');
    }

    setLaunchError('');
  };

  const selectCodebaseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    resetLaunchError();
    setHasAcceptedTerms(false);

    if (!file) {
      setCodebaseFile(null);
      setFileError('');
      return;
    }

    const validationError = getCodebaseZipValidationError(file);

    if (validationError) {
      setCodebaseFile(null);
      setFileError(validationError);
      event.target.value = '';
      return;
    }

    setCodebaseFile(file);
    setFileError('');
  };

  const launchScan = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (launchState === 'submitting' || nextScanAt) {
      return;
    }

    if (!codebaseFile) {
      codebaseInputRef.current?.click();
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setLaunchState('error');
      setLaunchError('Enter a valid email address.');
      return;
    }

    if (!hasAcceptedTerms) {
      setLaunchState('error');
      setLaunchError('Confirm your right to scan this codebase and accept the Terms of Service.');
      return;
    }

    const launchStartedAtMs = Date.now();

    setLaunchState('submitting');
    setLaunchError('');

    try {
      const payload = {
        resultEmail: trimmedEmail,
        codebaseUpload: {
          fileName: codebaseFile.name,
          sizeBytes: codebaseFile.size
        }
      };
      const allowance = await requestScanAllowance(payload);

      if (!allowance.canScan) {
        setLaunchState('idle');
        return;
      }

      const formData = new FormData();

      formData.append('payload', JSON.stringify(payload));
      formData.append('codebaseZip', codebaseFile, codebaseFile.name);

      const response = await requestFreeScan(formData);

      await waitForMinimumScanLaunchLoading(launchStartedAtMs);

      if (!response.runId) {
        throw new Error('Scan was accepted without a run ID.');
      }

      window.history.pushState({}, document.title, `/run/${encodeURIComponent(response.runId)}`);
      onNavigate();
    } catch (error) {
      await waitForMinimumScanLaunchLoading(launchStartedAtMs);
      console.error('Failed to launch scan:', error);
      setLaunchState('error');
      setLaunchError('Unable to start the scan. Please try again.');
    }
  };

  return (
    <>
    {isWarningVisible && (
      <div className="landing-warning-banner" role="status">
        <span>
          Due to high demand, our app is currently experiencing issues and may not work as expected. We’re investigating and working to resolve the problem as quickly as possible. Thanks for your patience.
        </span>
        <button
          type="button"
          className="landing-warning-banner-close"
          aria-label="Dismiss notice"
          onClick={() => setIsWarningVisible(false)}
        >
          ×
        </button>
      </div>
    )}
    <main className="landing-page" aria-labelledby="landing-title">
      <section className="landing-copy-panel">
        <a className="landing-brand" href="/" aria-label="Havu home">
          <img className="landing-brand-mark" src="/logoinvert.png" alt="" aria-hidden="true" />
          <span>Havu</span>
        </a>

        <div className="landing-copy">
          <h1 id="landing-title">Havu AI Cyber Security Scan</h1>
          <p>
            Run frontier security agents such as Daybreak and Astra against your codebase and receive a structured pentest report with aggregated findings ready for investors.
          </p>
        </div>

        <div className="landing-meta">
          <section className="security-tool-strip" aria-labelledby="security-tool-title">
            <h2 id="security-tool-title">Powered by trusted security tools</h2>
            <div className="security-tool-row" aria-label="Supported security tools">
              {SECURITY_TOOL_LOGOS.map((tool) => (
                <span className="security-tool-logo" key={tool.name}>
                  <img src={tool.src} alt={tool.name} loading="lazy" />
                  <span>{tool.name}</span>
                </span>
              ))}
            </div>
          </section>

          <div className="landing-affiliations" aria-label="Havu affiliations">
            <span className="landing-affiliation-item">
              Developed by SC Audit Studio
            </span>
            <a
              className="landing-affiliation-item"
              href="https://partners.circle.com/partner/sc-audit-studio"
              target="_blank"
              rel="noopener noreferrer"
            >
              Member of Circle alliance
            </a>
          </div>
        </div>
      </section>

      <aside className="landing-scan-panel" aria-label="Start a codebase scan">
        {launchState === 'submitting' ? (
          <ScanLaunchLoading />
        ) : (
          <div className="landing-scan-form">
            {nextScanAt ? (
              <div className="landing-form-heading" role="status">
                <h2>Your daily free scan has been used</h2>
                <p>You can run one free scan every 24 hours.</p>
                <p>Your next free scan is available on {new Date(nextScanAt).toLocaleString()}.</p>
              </div>
            ) : (
              <form className="landing-free-scan" onSubmit={launchScan}>
                <div className="landing-form-heading">
                  <h2>Free pentest</h2>
                  <p>Get a security report by email. One free scan per day.</p>
                </div>

                <label className="landing-upload-control" htmlFor="landing-codebase-zip">
                  <input
                    ref={codebaseInputRef}
                    id="landing-codebase-zip"
                    className="landing-upload-input"
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    onChange={selectCodebaseFile}
                  />
                  <span className="landing-upload-icon-wrap">
                    <CodebaseUploadIcon />
                  </span>
                  <span className="landing-upload-copy">
                    <strong>{codebaseFile ? 'Codebase ready' : 'Upload codebase'}</strong>
                    <span>{getSelectedFileLabel(codebaseFile)}</span>
                  </span>
                </label>
                {fileError && (
                  <p className="landing-form-error" role="alert">{fileError}</p>
                )}

                {codebaseFile && (
                  <div className="landing-reveal">
                    <label className="field landing-email-field">
                      <span>Send report to</span>
                      <input
                        ref={emailInputRef}
                        type="email"
                        value={email}
                        placeholder="you@example.com"
                        autoComplete="email"
                        required
                        onChange={(event) => {
                          setEmail(event.target.value);
                          resetLaunchError();
                        }}
                      />
                    </label>

                    <label className="landing-scan-authorization">
                      <input
                        type="checkbox"
                        required
                        checked={hasAcceptedTerms}
                        onChange={(event) => {
                          setHasAcceptedTerms(event.target.checked);
                          resetLaunchError();
                        }}
                      />
                      <span>
                        I have the right to scan this codebase and agree to the{' '}
                        <button type="button" className="landing-inline-link" onClick={() => setIsTermsOpen(true)}>
                          Terms of Service
                        </button>{' '}
                        and{' '}
                        <button type="button" className="landing-inline-link" onClick={() => setIsTermsOpen(true)}>
                          Privacy Policy
                        </button>
                        .
                      </span>
                    </label>
                  </div>
                )}

                {launchError && (
                  <div className="status-box error landing-launch-error" role="alert">
                    <span>{launchError}</span>
                  </div>
                )}

                <button type="submit" className="primary-button landing-submit-button">
                  Start free pentest
                  <ArrowRightIcon />
                </button>

                <p className="landing-ai-notice">
                  Includes two GLM 5.3 scans, two GPT 5.6 scans, and three security tools.
                </p>
              </form>
            )}

            <div className="landing-divider" role="separator">
              <span>or</span>
            </div>

            <section className="landing-advanced" aria-labelledby="landing-advanced-title">
              <div className="landing-advanced-copy">
                <h2 id="landing-advanced-title">Full pentest</h2>
                <p>Connect GitHub, choose the scope, and run all trusted tools in less time.</p>
              </div>
              <a className="secondary-button landing-login-button" href="/app">
                Log in
              </a>
            </section>
          </div>
        )}
      </aside>
    </main>
    <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </>
  );
}

export default LandingPage;
